import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { AnimatePresence, motion } from 'framer-motion';
import { MinusIcon, PlusIcon } from 'lucide-react';
import { getProduct } from '../../data/products';
import { Product } from '../../types/content';
import { Button } from '../ui/Button';
import { useBrand } from '../../contexts/BrandContext';
const featuredSlugs = ['semaglutide', 'tirzepatide'];
export function FeaturedProtocols() {
  const featured = featuredSlugs.map(slug => getProduct(slug)).filter((product): product is Product => Boolean(product));
  return <section className="bg-cream" data-id="element-38" data-mp="356a9zxtzug3w">
      <div className="mx-auto max-w-shell px-6 py-16 md:px-8 md:py-24" data-id="element-39" data-mp="357bwcyggmp0x">
        <div className="max-w-2xl" data-id="element-40" data-mp="3a4lmkgbob4pt">
          <p className="text-[0.6875rem] uppercase tracking-label text-brick" data-id="element-41" data-mp="3a5n5f6yxkvi3">
            Weight loss medication
          </p>
          <h2 className="mt-3 font-serif text-3xl font-bold leading-tight tracking-tight text-ink md:text-5xl" data-id="element-42" data-mp="13cujr21tj39g">
            Same price at any dose. Personalised to you.
          </h2>
          <p className="mt-5 text-lg leading-relaxed text-ink-soft" data-id="element-43" data-mp="uxtap2erx9zj">
            Two GLP-1s, both FDA-approved, both filled by US-licensed pharmacies. Here is everything
            people ask before they start — answered before you have to ask it.
          </p>
        </div>

        <div className="mt-12 grid gap-6 lg:grid-cols-2" data-id="element-44" data-mp="zrxlxxv2o46f">
          {featured.map(product => <FeaturedCard key={product.slug} product={product} data-id="element-45" data-mp="c1tj3sppk8gh" />)}
        </div>

        <p className="mt-8 max-w-3xl text-xs leading-relaxed text-ink-muted" data-id="element-46" data-mp="738qaecscr22">
          Membership required, billed at $39 a month and covering your doctor, your bloodwork and
          every dose change. Cancel from your account at any time.
        </p>
      </div>
    </section>;
}
function FeaturedCard({
  product
}: {
  product: Product;
}) {
  const {
    productImage
  } = useBrand();
  const [openIndex, setOpenIndex] = useState<number | null>(null);
  return <article className="flex flex-col overflow-hidden rounded-brand border border-ink/10 bg-cream-light" data-id="element-47" data-mp="p4mk3nl6415g">
      <div className="grid gap-6 p-6 sm:grid-cols-[0.8fr_1.2fr] sm:p-8" data-id="element-48" data-mp="u2xuq0jaefoq">
        <img src={productImage(product.slug, product.image)} alt={`${product.name}, ${product.form}`} className="aspect-square w-full rounded-brand bg-cream-deep object-cover" data-id="element-49" data-mp="u5ab499echzo" />
        <div className="flex flex-col" data-id="element-50" data-mp="6e4u3qrpzjns">
          <span className="self-start rounded-full bg-ink px-3 py-1 text-[0.625rem] uppercase tracking-label text-cream" data-id="element-51" data-mp="6f655v8qk65i">
            {product.status}
          </span>
          <h3 className="mt-4 font-serif text-3xl font-bold leading-none text-ink" data-id="element-52" data-mp="fe1br8fozi9c">
            {product.name}
          </h3>
          <p className="mt-3 font-serif text-4xl font-bold leading-none text-brick" data-id="element-53" data-mp="k62y5n6kqpd3">
            ${product.price}
            <span className="ml-1 text-base font-normal text-ink-muted" data-id="element-54" data-mp="3js9k2f8fp3mj">/mo</span>
          </p>
          <p className="mt-3 text-sm leading-relaxed text-ink-soft" data-id="element-55" data-mp="3nsth7it0c78o">
            Same price, any dose. Cancel any time.
          </p>
          <div className="mt-auto flex flex-wrap gap-3 pt-6" data-id="element-56" data-mp="3g8hfrebg0024">
            <Button to={`/get-started?product=${product.slug}`} data-id="element-57" data-mp="3bcabdpyp2wma">Get started</Button>
            <Link to={`/shop/${product.slug}`} className="inline-flex items-center rounded-full border border-ink/20 px-6 py-3 text-sm text-ink transition-colors duration-150 ease-republic hover:border-ink hover:bg-ink hover:text-cream" data-id="element-58" data-mp="37949euim7rlm">
              Full details
            </Link>
          </div>
        </div>
      </div>

      <ul className="border-t border-ink/10" data-id="element-59" data-mp="2nlwrwnu6e63b">
        {(product.faq ?? []).map((item, index) => {
        const isOpen = openIndex === index;
        return <li key={item.question} className="border-b border-ink/10 px-6 last:border-b-0 sm:px-8" data-id="element-60" data-mp="2jihzexp1vx8z" data-mpi={index}>
              <button type="button" onClick={() => setOpenIndex(isOpen ? null : index)} aria-expanded={isOpen} className="flex w-full items-start justify-between gap-6 py-4 text-left" data-id="element-61" data-mp="2enkr5szlhrx5">
                <span className="text-base font-medium text-ink" data-id="element-62" data-mp="ih1yx9jgzahs">{item.question}</span>
                {isOpen ? <MinusIcon className="mt-1 h-4 w-4 shrink-0 text-brick" strokeWidth={2} data-id="element-63" data-mp="3sxn8piu3z8i" /> : <PlusIcon className="mt-1 h-4 w-4 shrink-0 text-ink-muted" strokeWidth={2} data-id="element-64" data-mp="8qh3tw308o3m" />}
              </button>
              <AnimatePresence initial={false} data-id="element-65" data-mp="3uzi97uu18gyo">
                {isOpen && <motion.div initial={{
              height: 0,
              opacity: 0
            }} animate={{
              height: 'auto',
              opacity: 1
            }} exit={{
              height: 0,
              opacity: 0
            }} transition={{
              duration: 0.22,
              ease: [0.23, 1, 0.32, 1]
            }} className="overflow-hidden" data-id="element-66" data-mp="3uxffp0to85p7">
                    <p className="max-w-xl pb-5 text-sm leading-relaxed text-ink-soft" data-id="element-67" data-mp="1k6nweaot9dp0">
                      {item.answer}
                    </p>
                  </motion.div>}
              </AnimatePresence>
            </li>;
      })}
      </ul>
    </article>;
}